import { z } from 'zod';
import {
  MAX_FIXTURE_COUNT,
  MAX_MEASURED_SQUARE_FEET,
  QUOTE_SEGMENTS,
  ROOM_CONDITION_CODES,
  ROOM_SIZE_CODES,
  SERIES_FREQUENCIES,
  type RoomConditionCode,
  type RoomSizeCode,
} from './constants/index.ts';
import { idSchema } from './schemas/common.ts';
import { dateTimeFormat } from './schemas/formats.ts';

/**
 * Canonical Quick Quote v1 wire and persisted text-flow state.
 *
 * Quick Quote takes one bounded text paragraph, photos, or both, and opaque
 * answer IDs. Photos never travel in this flow: each is uploaded and read on
 * its own first (`/quick-quotes/photos`), and a Quick Quote names the photos
 * it starts with by id. Only their tags reach the parse, never the images.
 * Room/task/measurement authority is stored in the shared Magic Quote
 * relational aggregate; the state document below owns only the clarification
 * flow.
 */

export const QUICK_QUOTE_SCHEMA_VERSION = 1 as const;
export const QUICK_QUOTE_MAX_PHYSICAL_ROOMS = 40 as const;
export const QUICK_QUOTE_MAX_QUESTIONS = 3 as const;
export const QUICK_QUOTE_MAX_CLARIFICATION_ROUNDS = 2 as const;
export const QUICK_QUOTE_MAX_FOLLOW_UP_ROUNDS = 2 as const;
export const QUICK_QUOTE_MAX_PROMPT_CHARS = 4_000 as const;
export const QUICK_QUOTE_MAX_ANSWER_CHARS = 1_000 as const;
export const QUICK_QUOTE_MAX_OPAQUE_ID_CHARS = 255 as const;
export const QUICK_QUOTE_COVERAGE_QUESTION_ID = 'room_check' as const;
/** The most photos one Quick Quote takes. */
export const QUICK_QUOTE_MAX_PHOTOS = 15 as const;
/**
 * How much a company can store in Quick Quote photos in a day, in megabytes.
 * A phone photo is redrawn to about half a megabyte before it is sent.
 */
export const QUICK_QUOTE_PHOTO_DAILY_MEGABYTES = 1024;
/** What one photo's read costs, in AI credits (the `quick_quote_photo` action). */
export const QUICK_QUOTE_PHOTO_CREDITS = 1 as const;
/** The shortest description that starts a Quick Quote without photos. */
export const QUICK_QUOTE_MIN_PROMPT_CHARS = 10 as const;

export const QUICK_QUOTE_STATUSES = ['clarifying', 'ready', 'generating', 'generated', 'abandoned'] as const;
export type QuickQuoteStatus = (typeof QUICK_QUOTE_STATUSES)[number];
export type QuickQuoteSegment = (typeof QUOTE_SEGMENTS)[number];
export type QuickQuoteFrequency = (typeof SERIES_FREQUENCIES)[number];
export type QuickQuoteLocale = 'en' | 'es';
/** Compatibility exports used by existing web/mobile imports. */
export const QUICK_QUOTE_FREQUENCIES = SERIES_FREQUENCIES;
export const QUICK_QUOTE_SIZE_CODES = ROOM_SIZE_CODES as readonly [RoomSizeCode, ...RoomSizeCode[]];
export const QUICK_QUOTE_CONDITION_CODES = ROOM_CONDITION_CODES as readonly [RoomConditionCode, ...RoomConditionCode[]];
export const QUICK_QUOTE_UNCERTAINTY_FIELDS = [
  'square_feet',
  'bedrooms',
  'bathrooms',
  'frequency',
  'visits_per_week',
  'condition',
] as const;
export type QuickQuoteUncertaintyField = (typeof QUICK_QUOTE_UNCERTAINTY_FIELDS)[number];

const uniqueIds = (ids: readonly string[]): boolean => new Set(ids).size === ids.length;

export const quickQuotePropertyFactsSchema = z
  .object({
    squareFeet: z.number().int().min(1).max(MAX_MEASURED_SQUARE_FEET).nullable(),
    bedrooms: z.number().int().min(0).max(100).nullable(),
    bathrooms: z
      .number()
      .min(0)
      .max(100)
      .refine((value) => Number.isInteger(value * 2), 'Bathrooms must use whole or half increments')
      .nullable(),
    frequency: z.enum(SERIES_FREQUENCIES).nullable(),
    /** Required before commercial weekly work is ready; null everywhere else. */
    visitsPerWeek: z.number().int().min(1).max(7).nullable().default(null),
    condition: z.enum(ROOM_CONDITION_CODES).nullable(),
    assumptions: z.array(z.string().trim().min(1).max(300)).max(20),
    unrecognized: z.array(z.string().trim().min(1).max(300)).max(20),
  })
  .strict();
export type QuickQuotePropertyFacts = z.infer<typeof quickQuotePropertyFactsSchema>;

export const quickQuoteMeasurementItemSchema = z.discriminatedUnion('basis', [
  z
    .object({
      serviceTemplateId: idSchema,
      name: z.string().trim().min(1).max(160),
      basis: z.literal('area'),
      value: z.number().positive().max(MAX_MEASURED_SQUARE_FEET),
      unit: z.literal('square_feet'),
      source: z.enum(['prompt', 'answer']),
    })
    .strict(),
  z
    .object({
      serviceTemplateId: idSchema,
      name: z.string().trim().min(1).max(160),
      basis: z.literal('fixture'),
      value: z.number().int().positive().max(MAX_FIXTURE_COUNT),
      unit: z.literal('fixtures'),
      source: z.enum(['prompt', 'answer']),
    })
    .strict(),
]);

export const quickQuoteMeasurementsSchema = z
  .array(quickQuoteMeasurementItemSchema)
  .max(200)
  .refine(
    (measurements) => uniqueIds(measurements.map((measurement) => measurement.serviceTemplateId)),
    'Only one measurement may be stored for each service',
  );
/** Compatibility name: historically this export is the measurement array. */
export const quickQuoteMeasurementSchema = quickQuoteMeasurementsSchema;
export type QuickQuoteMeasurement = z.infer<typeof quickQuoteMeasurementItemSchema>;

export const quickQuoteRoomSchema = z
  .object({
    /** Server-owned stable ID. Model-authored IDs are never persisted. */
    id: idSchema,
    roomTemplateId: idSchema,
    name: z.string().trim().min(1).max(120),
    quantity: z.number().int().min(1).max(QUICK_QUOTE_MAX_PHYSICAL_ROOMS),
    sizeCode: z.enum(ROOM_SIZE_CODES as readonly [RoomSizeCode, ...RoomSizeCode[]]),
    conditionCode: z.enum(ROOM_CONDITION_CODES as readonly [RoomConditionCode, ...RoomConditionCode[]]),
    serviceTemplateIds: z.array(idSchema).max(200).refine(uniqueIds, 'A service may be selected only once'),
    source: z.enum(['explicit', 'confirmed', 'assumed']),
    /** Visible in Room Check and the fingerprint, absent from quote inputs. */
    excluded: z.boolean(),
    /** Actual per-room/per-task units. Property square feet never appear here. */
    measurements: quickQuoteMeasurementsSchema,
  })
  .strict()
  .superRefine((room, context) => {
    const selected = new Set(room.serviceTemplateIds);
    for (const [index, measurement] of room.measurements.entries()) {
      if (!selected.has(measurement.serviceTemplateId)) {
        context.addIssue({
          code: 'custom',
          path: ['measurements', index, 'serviceTemplateId'],
          message: 'A measurement must belong to a selected service',
        });
      }
    }
  });
export type QuickQuoteRoom = z.infer<typeof quickQuoteRoomSchema>;

export const quickQuoteRoomsSchema = z
  .array(quickQuoteRoomSchema)
  .max(QUICK_QUOTE_MAX_PHYSICAL_ROOMS)
  .superRefine((rooms, context) => {
    if (!uniqueIds(rooms.map((room) => room.id))) {
      context.addIssue({ code: 'custom', message: 'Room IDs must be unique' });
    }
    const physicalCount = rooms.reduce((sum, room) => sum + room.quantity, 0);
    if (physicalCount > QUICK_QUOTE_MAX_PHYSICAL_ROOMS) {
      context.addIssue({
        code: 'custom',
        message: `Quick Quote supports at most ${QUICK_QUOTE_MAX_PHYSICAL_ROOMS} physical rooms`,
      });
    }
  });

export const quickQuoteOptionSchema = z
  .object({
    id: z.string().trim().min(1).max(QUICK_QUOTE_MAX_OPAQUE_ID_CHARS),
    label: z.string().trim().min(1).max(160),
    roomTemplateId: idSchema.optional(),
  })
  .strict();
export type QuickQuoteOption = z.infer<typeof quickQuoteOptionSchema>;

export const quickQuoteQuestionSchema = z
  .object({
    id: z.string().trim().min(1).max(QUICK_QUOTE_MAX_OPAQUE_ID_CHARS),
    kind: z.enum(['single_select', 'multi_select', 'number', 'short_text', 'room_check']),
    prompt: z.string().trim().min(1).max(500),
    required: z.boolean(),
    options: z
      .array(quickQuoteOptionSchema)
      .max(12)
      .refine((options) => uniqueIds(options.map((option) => option.id)), 'Question option IDs must be unique'),
    /** Arbitrary prose may use AI only while the clarification cap remains. */
    allowText: z.boolean(),
  })
  .strict()
  .superRefine((question, context) => {
    const select = question.kind === 'single_select' || question.kind === 'multi_select';
    if (select && question.options.length === 0) {
      context.addIssue({ code: 'custom', path: ['options'], message: 'Select questions need at least one option' });
    }
    if ((question.kind === 'number' || question.kind === 'short_text') && question.options.length !== 0) {
      context.addIssue({ code: 'custom', path: ['options'], message: 'Text and number questions cannot have options' });
    }
    if ((question.id === QUICK_QUOTE_COVERAGE_QUESTION_ID) !== (question.kind === 'room_check')) {
      context.addIssue({ code: 'custom', path: ['id'], message: 'Room Check uses its reserved opaque ID' });
    }
    if (question.kind === 'room_check') {
      const roomIds = question.options.map((option) => option.roomTemplateId);
      if (roomIds.some((id) => !id) || new Set(roomIds).size !== roomIds.length) {
        context.addIssue({
          code: 'custom',
          path: ['options'],
          message: 'Room Check options need unique room template IDs',
        });
      }
    }
    if (question.kind === 'short_text' && !question.allowText) {
      context.addIssue({ code: 'custom', path: ['allowText'], message: 'Short text questions must allow text' });
    }
  });
export type QuickQuoteQuestion = z.infer<typeof quickQuoteQuestionSchema>;

export const quickQuoteCoverageSchema = z
  .object({
    confirmed: z.boolean(),
    confirmedAt: z.string().datetime(dateTimeFormat()).nullable(),
    questionId: z.literal(QUICK_QUOTE_COVERAGE_QUESTION_ID).nullable(),
    /** Localized human-readable blockers; never internal uncertainty tokens. */
    unresolved: z.array(z.string().trim().min(1).max(500)).max(20),
    canConfirm: z.boolean().default(false),
  })
  .strict();
export type QuickQuoteCoverage = z.infer<typeof quickQuoteCoverageSchema>;

export const quickQuoteCoverageDraftSchema = z
  .object({
    questionId: z.literal(QUICK_QUOTE_COVERAGE_QUESTION_ID).nullable(),
    unresolved: z.array(z.string().trim().min(1).max(500)).max(20),
    canConfirm: z.boolean().default(false),
  })
  .strict();

export const quickQuoteStateSchema = z
  .object({
    schemaVersion: z.literal(QUICK_QUOTE_SCHEMA_VERSION),
    version: z.number().int().min(1),
    phase: z.enum(['parsing', 'clarifying', 'ready', 'generating', 'generated', 'abandoned']),
    segment: z.enum(QUOTE_SEGMENTS),
    locale: z.enum(['en', 'es']),
    promptCaptureId: idSchema,
    summary: z.string().trim().min(1).max(500).nullable(),
    propertyFacts: quickQuotePropertyFactsSchema,
    questions: z
      .array(quickQuoteQuestionSchema)
      .max(QUICK_QUOTE_MAX_QUESTIONS)
      .refine((questions) => uniqueIds(questions.map((question) => question.id)), 'Question IDs must be unique'),
    uncertaintyFields: z
      .array(z.enum(QUICK_QUOTE_UNCERTAINTY_FIELDS))
      .max(QUICK_QUOTE_UNCERTAINTY_FIELDS.length)
      .refine(uniqueIds, 'Uncertainty fields must be unique')
      .default([]),
    rankedSuggestedRoomTemplateIds: z
      .array(idSchema)
      .max(6)
      .refine(uniqueIds, 'Suggested room IDs must be unique')
      .default([]),
    coverage: quickQuoteCoverageDraftSchema,
    /** Compatibility-only; relational excluded rooms are current authority. */
    excludedRoomTemplateIds: z
      .array(idSchema)
      .max(40)
      .refine(uniqueIds, 'Excluded room IDs must be unique')
      .default([]),
    libraryFingerprint: z
      .string()
      .regex(/^[a-f0-9]{64}$/)
      .nullable(),
    followUpRoundsUsed: z.number().int().min(0).max(QUICK_QUOTE_MAX_FOLLOW_UP_ROUNDS).default(0),
    clarificationRoundsUsed: z.number().int().min(0).max(QUICK_QUOTE_MAX_CLARIFICATION_ROUNDS),
    /** Compatibility-only capture hints; Quick v1 remains text-only. */
    evidenceCaptureIds: z.array(idSchema).max(20).refine(uniqueIds, 'Evidence capture IDs must be unique').default([]),
    /** Compatibility-only prior operation pointer; the operation ledger wins. */
    lastOperationId: z.string().trim().min(8).max(QUICK_QUOTE_MAX_OPAQUE_ID_CHARS).nullable().default(null),
    lastError: z.string().trim().max(500).nullable(),
  })
  .strict()
  .superRefine((state, context) => {
    if (state.clarificationRoundsUsed > state.followUpRoundsUsed) {
      context.addIssue({
        code: 'custom',
        path: ['clarificationRoundsUsed'],
        message: 'AI clarification rounds cannot exceed submitted follow-up rounds',
      });
    }
    if (
      state.clarificationRoundsUsed >= QUICK_QUOTE_MAX_CLARIFICATION_ROUNDS ||
      state.followUpRoundsUsed >= QUICK_QUOTE_MAX_FOLLOW_UP_ROUNDS
    ) {
      state.questions.forEach((question, index) => {
        if (question.allowText) {
          context.addIssue({
            code: 'custom',
            path: ['questions', index, 'allowText'],
            message: 'Arbitrary text must be disabled at either Quick Quote round cap',
          });
        }
      });
    }
    const visits = state.propertyFacts.visitsPerWeek;
    if (visits !== null && (state.segment !== 'commercial' || state.propertyFacts.frequency !== 'weekly')) {
      context.addIssue({
        code: 'custom',
        path: ['propertyFacts', 'visitsPerWeek'],
        message: 'Visits per week only applies to commercial weekly cleaning',
      });
    }
    if (
      ['ready', 'generating', 'generated'].includes(state.phase) &&
      state.segment === 'commercial' &&
      state.propertyFacts.frequency === 'weekly' &&
      visits === null
    ) {
      context.addIssue({
        code: 'custom',
        path: ['propertyFacts', 'visitsPerWeek'],
        message: 'Ready commercial weekly Quick Quotes need a durable visit count',
      });
    }
    if (['ready', 'generating', 'generated'].includes(state.phase) && state.questions.length !== 0) {
      context.addIssue({
        code: 'custom',
        path: ['questions'],
        message: 'A terminal or generation-ready Quick Quote cannot have pending questions',
      });
    }
    // Cached canConfirm is compatibility/display data only. Fresh relational
    // coverage is the sole serializer/generation authority.
  });
export type QuickQuoteState = z.infer<typeof quickQuoteStateSchema>;

export function parseQuickQuoteState(stored: string | null | undefined): QuickQuoteState | null {
  if (!stored) return null;
  try {
    const raw = JSON.parse(stored) as unknown;
    let candidate = raw;
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      const state = raw as Record<string, unknown>;
      const facts = state.propertyFacts;
      if (
        state.schemaVersion === 1 &&
        ['ready', 'generating', 'generated'].includes(String(state.phase)) &&
        state.segment === 'commercial' &&
        facts &&
        typeof facts === 'object' &&
        !Array.isArray(facts) &&
        (facts as Record<string, unknown>).frequency === 'weekly' &&
        ((facts as Record<string, unknown>).visitsPerWeek === null ||
          (facts as Record<string, unknown>).visitsPerWeek === undefined)
      ) {
        const factRecord = facts as Record<string, unknown>;
        const assumptions = Array.isArray(factRecord.assumptions) ? [...factRecord.assumptions] : [];
        const message =
          state.locale === 'es'
            ? 'Se asumió una visita por semana para compatibilidad.'
            : 'Assumed one visit per week for compatibility.';
        const compatibleAssumptions = [message, ...assumptions.filter((item) => item !== message)].slice(0, 20);
        candidate = {
          ...state,
          propertyFacts: { ...factRecord, visitsPerWeek: 1, assumptions: compatibleAssumptions },
        };
      }
    }
    const parsed = quickQuoteStateSchema.safeParse(candidate);
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export const quickQuoteCreateSchema = z
  .object({
    customerId: idSchema,
    propertyId: idSchema.nullable().optional(),
    /** Required; Quick generation never infers segment from a company name. */
    segment: z.enum(QUOTE_SEGMENTS),
    /** May be empty when photos describe the home on their own. */
    prompt: z.string().trim().max(QUICK_QUOTE_MAX_PROMPT_CHARS),
    /** Photos already uploaded and read (`POST /quick-quotes/photos`), in the order they were taken. */
    photoIds: z
      .array(idSchema)
      .max(QUICK_QUOTE_MAX_PHOTOS, `A Quick Quote takes up to ${QUICK_QUOTE_MAX_PHOTOS} photos`)
      .refine(uniqueIds, 'Add each photo only once')
      .optional(),
    locale: z.enum(['en', 'es']).optional(),
    clientRequestId: z.string().trim().min(8).max(64),
  })
  .strict()
  .refine((input) => input.prompt.length >= QUICK_QUOTE_MIN_PROMPT_CHARS || (input.photoIds?.length ?? 0) > 0, {
    message: 'Describe the property in a little more detail, or add photos',
    path: ['prompt'],
  });
export type QuickQuoteCreateInput = z.infer<typeof quickQuoteCreateSchema>;

export const quickQuoteAnswerSchema = z
  .object({
    questionId: z.string().trim().min(1).max(QUICK_QUOTE_MAX_OPAQUE_ID_CHARS),
    optionIds: z
      .array(z.string().trim().min(1).max(QUICK_QUOTE_MAX_OPAQUE_ID_CHARS))
      .max(12)
      .refine(uniqueIds, 'Choose each option only once')
      .optional(),
    text: z.string().trim().min(1).max(QUICK_QUOTE_MAX_ANSWER_CHARS).optional(),
  })
  .strict()
  .refine((answer) => (answer.optionIds?.length ?? 0) > 0 || Boolean(answer.text), {
    message: 'Choose an answer or type a correction',
  })
  .refine(
    (answer) =>
      answer.questionId === QUICK_QUOTE_COVERAGE_QUESTION_ID ||
      !((answer.optionIds?.length ?? 0) > 0 && Boolean(answer.text)),
    { message: 'Submit either opaque options or text for this question, not both' },
  );
export type QuickQuoteAnswer = z.infer<typeof quickQuoteAnswerSchema>;

export const answerQuickQuoteSchema = z
  .object({
    version: z.number().int().min(1),
    answers: z.array(quickQuoteAnswerSchema).max(QUICK_QUOTE_MAX_QUESTIONS),
    coverageConfirmed: z.boolean().optional(),
    clientRequestId: z.string().trim().min(8).max(64),
  })
  .strict()
  .refine((body) => body.answers.length > 0 || body.coverageConfirmed === true, {
    message: 'Answer a question or confirm the Room Check',
  })
  .refine((body) => uniqueIds(body.answers.map((answer) => answer.questionId)), {
    message: 'Answer each question only once',
  })
  .refine(
    (body) =>
      body.coverageConfirmed !== true ||
      !body.answers.some((answer) => answer.questionId === QUICK_QUOTE_COVERAGE_QUESTION_ID && Boolean(answer.text)),
    { message: 'A typed room correction must be reviewed before confirming Room Check' },
  );
export type AnswerQuickQuoteInput = z.infer<typeof answerQuickQuoteSchema>;

export const generateQuickQuoteSchema = z
  .object({
    version: z.number().int().min(1),
    clientRequestId: z.string().trim().min(8).max(64),
  })
  .strict();
export type GenerateQuickQuoteInput = z.infer<typeof generateQuickQuoteSchema>;

export const quickQuoteRequestIdSchema = z.string().trim().min(8).max(64);

/** The client's own id for one photo, so a retried upload is stored once. */
export const quickQuoteClientPhotoIdSchema = z.string().trim().min(8).max(64);

/** Whether, and on what terms, this company's Quick Quote takes photos. */
export const quickQuotePhotoCapabilitiesSchema = z
  .object({
    available: z.boolean(),
    unavailableReason: z.enum(['plan', 'ai_off', 'feature_off', 'unconfigured']).nullable(),
    maxPhotos: z.literal(QUICK_QUOTE_MAX_PHOTOS),
    creditsPerPhoto: z.literal(QUICK_QUOTE_PHOTO_CREDITS),
  })
  .strict();
export type QuickQuotePhotoCapabilities = z.infer<typeof quickQuotePhotoCapabilitiesSchema>;

export const quickQuoteCapabilitiesSchema = z
  .object({
    available: z.boolean(),
    unavailableReason: z.enum(['plan', 'ai_off', 'feature_off', 'unconfigured']).nullable(),
    feature: z.literal('quick_quote'),
    requiredPlan: z.literal('starter'),
    costs: z
      .object({
        initialParse: z.literal(4),
        freeTextClarification: z.literal(2),
        structuredAnswers: z.literal(0),
        generate: z.literal(0),
      })
      .strict(),
    limits: z
      .object({
        promptChars: z.literal(4000),
        answerTextChars: z.literal(1000),
        maxQuestionsPerRound: z.literal(3),
        maxClarificationRounds: z.literal(2),
        maxRooms: z.literal(40),
      })
      .strict(),
    photos: quickQuotePhotoCapabilitiesSchema,
  })
  .strict();
export type QuickQuoteCapabilities = z.infer<typeof quickQuoteCapabilitiesSchema>;

/**
 * A photo's read: `reading` until the model has answered; `read` with its
 * tags; `hidden` when it is kept out of the parse (a person, a document, a
 * repeat, or not of a home); `unreadable` when the model could not use it;
 * `skipped` when it was never read (too large, or the AI allowance was spent).
 * Only `read` photos reach the parse.
 */
export const QUICK_QUOTE_PHOTO_STATUSES = ['reading', 'read', 'hidden', 'unreadable', 'skipped'] as const;
export type QuickQuotePhotoStatus = (typeof QUICK_QUOTE_PHOTO_STATUSES)[number];

export interface QuickQuotePhoto {
  id: string;
  clientPhotoId: string;
  status: QuickQuotePhotoStatus;
  hiddenReason: 'person' | 'document' | 'duplicate' | 'irrelevant' | null;
  /**
   * Why it was not read: `limit` (the month's AI credits), `daily_cap` (today's
   * AI cap, which clears tomorrow), `unavailable` (AI or Quick Quote switched
   * off), `too_large` or `missing`; `refused` and `unreadable` show as the
   * `unreadable` status.
   */
  skipReason: string | null;
  /** What the read saw, for the photo's caption. */
  roomKind: string | null;
  descriptor: string | null;
  conditionCode: string | null;
  /** The photo's bytes, for its thumbnail. */
  mediaUrl: string;
  sizeBytes: number;
  /** What its read cost; also counted in its Quick Quote's `billedCredits`. */
  creditsCharged: number;
  createdAt: string;
}

/**
 * Why a photo was not read, where reading it again can work: nothing was
 * charged, and each clears (credits added, the next day, AI switched back on).
 */
export const QUICK_QUOTE_PHOTO_READ_AGAIN_REASONS = ['limit', 'daily_cap', 'unavailable'] as const;

/** A waiting photo that `POST /quick-quotes/photos/:photoId/read` can read again. */
export function canReadQuickQuotePhotoAgain(photo: Pick<QuickQuotePhoto, 'status' | 'skipReason'>): boolean {
  return (
    photo.status === 'skipped' &&
    (QUICK_QUOTE_PHOTO_READ_AGAIN_REASONS as readonly (string | null)[]).includes(photo.skipReason)
  );
}

export interface QuickQuoteSession {
  id: string;
  status: QuickQuoteStatus;
  version: number;
  customerId: string;
  propertyId: string | null;
  segment: QuickQuoteSegment;
  locale: QuickQuoteLocale;
  prompt: string;
  summary: string | null;
  propertyFacts: QuickQuotePropertyFacts;
  /** Relational scope, including explicit exclusions and measurements. */
  rooms: QuickQuoteRoom[];
  questions: QuickQuoteQuestion[];
  coverage: QuickQuoteCoverage;
  canGenerate: boolean;
  quoteId: string | null;
  billedCredits: number;
  /** The photos this Quick Quote started with, in order; empty for a typed one. */
  photos: QuickQuotePhoto[];
  clarificationRoundsUsed: number;
  followUpRoundsUsed: number;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GenerateQuickQuoteResponse {
  quoteId: string;
  /** A successful Quick Quote is all-or-nothing. This is always empty. */
  skipped: [];
  session: QuickQuoteSession;
}

export interface QuickQuoteDataEnvelope<T> {
  data: T;
  requestId: string;
}
